import { cn } from "@utils/tw"
import { createContext, forwardRef, HTMLProps, useContext } from "react"

type Breakpoint = "initial" | "bigPhone" | "tablet" | "laptop" | "desktop" | "cinema"
type ColSize = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12

const BP_PREFIX: Record<Breakpoint, string> = {
  initial: "",
  bigPhone: "sm:",
  tablet: "md:",
  laptop: "lg:",
  desktop: "xl:",
  cinema: "xxl:",
}

/**
 * Col builds Tailwind class names by string concatenation below, which
 * Tailwind's content scanner can't follow (it only picks up classes that
 * appear as literal text somewhere in a scanned file). This block exists
 * purely so every combination Col could concatenate is guaranteed to have
 * real CSS generated for it — it's never read at runtime.
 */
export const __tailwindGridSafelist = `
  col-span-1 col-span-2 col-span-3 col-span-4 col-span-5 col-span-6 col-span-7 col-span-8 col-span-9 col-span-10 col-span-11 col-span-12
  sm:col-span-1 sm:col-span-2 sm:col-span-3 sm:col-span-4 sm:col-span-5 sm:col-span-6 sm:col-span-7 sm:col-span-8 sm:col-span-9 sm:col-span-10 sm:col-span-11 sm:col-span-12
  md:col-span-1 md:col-span-2 md:col-span-3 md:col-span-4 md:col-span-5 md:col-span-6 md:col-span-7 md:col-span-8 md:col-span-9 md:col-span-10 md:col-span-11 md:col-span-12
  lg:col-span-1 lg:col-span-2 lg:col-span-3 lg:col-span-4 lg:col-span-5 lg:col-span-6 lg:col-span-7 lg:col-span-8 lg:col-span-9 lg:col-span-10 lg:col-span-11 lg:col-span-12
  xl:col-span-1 xl:col-span-2 xl:col-span-3 xl:col-span-4 xl:col-span-5 xl:col-span-6 xl:col-span-7 xl:col-span-8 xl:col-span-9 xl:col-span-10 xl:col-span-11 xl:col-span-12
  xxl:col-span-1 xxl:col-span-2 xxl:col-span-3 xxl:col-span-4 xxl:col-span-5 xxl:col-span-6 xxl:col-span-7 xxl:col-span-8 xxl:col-span-9 xxl:col-span-10 xxl:col-span-11 xxl:col-span-12
  col-start-1 col-start-2 col-start-3 col-start-4 col-start-5 col-start-6 col-start-7 col-start-8 col-start-9 col-start-10 col-start-11 col-start-12
  sm:col-start-1 sm:col-start-2 sm:col-start-3 sm:col-start-4 sm:col-start-5 sm:col-start-6 sm:col-start-7 sm:col-start-8 sm:col-start-9 sm:col-start-10 sm:col-start-11 sm:col-start-12
  md:col-start-1 md:col-start-2 md:col-start-3 md:col-start-4 md:col-start-5 md:col-start-6 md:col-start-7 md:col-start-8 md:col-start-9 md:col-start-10 md:col-start-11 md:col-start-12
  lg:col-start-1 lg:col-start-2 lg:col-start-3 lg:col-start-4 lg:col-start-5 lg:col-start-6 lg:col-start-7 lg:col-start-8 lg:col-start-9 lg:col-start-10 lg:col-start-11 lg:col-start-12
  xl:col-start-1 xl:col-start-2 xl:col-start-3 xl:col-start-4 xl:col-start-5 xl:col-start-6 xl:col-start-7 xl:col-start-8 xl:col-start-9 xl:col-start-10 xl:col-start-11 xl:col-start-12
  xxl:col-start-1 xxl:col-start-2 xxl:col-start-3 xxl:col-start-4 xxl:col-start-5 xxl:col-start-6 xxl:col-start-7 xxl:col-start-8 xxl:col-start-9 xxl:col-start-10 xxl:col-start-11 xxl:col-start-12
  col-end-1 col-end-2 col-end-3 col-end-4 col-end-5 col-end-6 col-end-7 col-end-8 col-end-9 col-end-10 col-end-11 col-end-12 col-end-13
  sm:col-end-1 sm:col-end-2 sm:col-end-3 sm:col-end-4 sm:col-end-5 sm:col-end-6 sm:col-end-7 sm:col-end-8 sm:col-end-9 sm:col-end-10 sm:col-end-11 sm:col-end-12 sm:col-end-13
  md:col-end-1 md:col-end-2 md:col-end-3 md:col-end-4 md:col-end-5 md:col-end-6 md:col-end-7 md:col-end-8 md:col-end-9 md:col-end-10 md:col-end-11 md:col-end-12 md:col-end-13
  lg:col-end-1 lg:col-end-2 lg:col-end-3 lg:col-end-4 lg:col-end-5 lg:col-end-6 lg:col-end-7 lg:col-end-8 lg:col-end-9 lg:col-end-10 lg:col-end-11 lg:col-end-12 lg:col-end-13
  xl:col-end-1 xl:col-end-2 xl:col-end-3 xl:col-end-4 xl:col-end-5 xl:col-end-6 xl:col-end-7 xl:col-end-8 xl:col-end-9 xl:col-end-10 xl:col-end-11 xl:col-end-12 xl:col-end-13
  xxl:col-end-1 xxl:col-end-2 xxl:col-end-3 xxl:col-end-4 xxl:col-end-5 xxl:col-end-6 xxl:col-end-7 xxl:col-end-8 xxl:col-end-9 xxl:col-end-10 xxl:col-end-11 xxl:col-end-12 xxl:col-end-13
  w-col w-cols-2 w-cols-3 w-cols-4 w-cols-5 w-cols-6 w-cols-7 w-cols-8 w-cols-9 w-cols-10 w-cols-11 w-cols-12
  sm:w-col sm:w-cols-2 sm:w-cols-3 sm:w-cols-4 sm:w-cols-5 sm:w-cols-6 sm:w-cols-7 sm:w-cols-8 sm:w-cols-9 sm:w-cols-10 sm:w-cols-11 sm:w-cols-12
  md:w-col md:w-cols-2 md:w-cols-3 md:w-cols-4 md:w-cols-5 md:w-cols-6 md:w-cols-7 md:w-cols-8 md:w-cols-9 md:w-cols-10 md:w-cols-11 md:w-cols-12
  lg:w-col lg:w-cols-2 lg:w-cols-3 lg:w-cols-4 lg:w-cols-5 lg:w-cols-6 lg:w-cols-7 lg:w-cols-8 lg:w-cols-9 lg:w-cols-10 lg:w-cols-11 lg:w-cols-12
  xl:w-col xl:w-cols-2 xl:w-cols-3 xl:w-cols-4 xl:w-cols-5 xl:w-cols-6 xl:w-cols-7 xl:w-cols-8 xl:w-cols-9 xl:w-cols-10 xl:w-cols-11 xl:w-cols-12
  xxl:w-col xxl:w-cols-2 xxl:w-cols-3 xxl:w-cols-4 xxl:w-cols-5 xxl:w-cols-6 xxl:w-cols-7 xxl:w-cols-8 xxl:w-cols-9 xxl:w-cols-10 xxl:w-cols-11 xxl:w-cols-12
` as const

const GridModeContext = createContext<"grid" | "flex">("grid")

type GridProps = HTMLProps<"div"> & {
  align?: "center" | "end"
  mode?: "grid" | "flex"
}

/**
 * Specifies a 12 column grid, using the responsive grid tokens configured
 * in tailwind.config.ts (see the `grid` key passed to `responsiveTheme`).
 */
export const Grid = forwardRef<HTMLDivElement, GridProps>(
  ({ align, mode = "grid", className, children, ...props }, ref) => (
    <GridModeContext.Provider value={mode}>
      <div
        ref={ref}
        className={cn(
          "--grid",
          mode === "flex" ? "flex flex-wrap gap-[var(--tw-grid-gutter)]" : "grid-auto",
          align === "center" && "items-center",
          align === "end" && "items-end",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </GridModeContext.Provider>
  ),
)
Grid.displayName = "Grid"

type ContainerProps = HTMLProps<"div">

/** Creates a centered div, with the same width as the grid */
export const Container = forwardRef<HTMLDivElement, ContainerProps>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("grid-container z-0", className)} {...props} />
))
Container.displayName = "Container"

type OffsetNames<T extends string> = `${T}Offset`
type ColProps = HTMLProps<"div"> & {
  [key in Breakpoint]?: ColSize
} & {
  [key in OffsetNames<Breakpoint>]?: ColSize
}

const BREAKPOINT_KEYS = Object.keys(BP_PREFIX) as Breakpoint[]

export const Col = forwardRef<HTMLDivElement, ColProps>(({ className, ...props }, ref) => {
  const mode = useContext(GridModeContext)
  const classes: string[] = []
  const passthroughProps: Record<string, unknown> = {}

  for (const key in props) {
    const offsetMatch = BREAKPOINT_KEYS.find((bp) => key === `${bp}Offset`)
    const bp = offsetMatch ?? (BREAKPOINT_KEYS.includes(key as Breakpoint) ? (key as Breakpoint) : null)

    if (!bp) {
      passthroughProps[key] = (props as any)[key]
      continue
    }

    const value = (props as any)[key] as ColSize
    const prefix = BP_PREFIX[bp]

    if (offsetMatch) {
      // Longhand col-start/col-end, not the col-span shorthand, so an
      // offset column doesn't fight a plain span column over the
      // `grid-column` shorthand property when both classes are present.
      classes.push(`${prefix}col-start-${value + 1}`)
    } else if (mode === "flex") {
      classes.push(value === 1 ? `${prefix}w-col` : `${prefix}w-cols-${value}`)
    } else {
      classes.push(`${prefix}col-span-${value}`)
    }
  }

  // If this column has an offset, pair it with an explicit end instead of
  // relying on col-span (see above).
  for (const bp of BREAKPOINT_KEYS) {
    const offsetValue = (props as any)[`${bp}Offset`] as ColSize | undefined
    const spanValue = (props as any)[bp] as ColSize | undefined
    if (offsetValue !== undefined && spanValue !== undefined) {
      classes.push(`${BP_PREFIX[bp]}col-end-${offsetValue + spanValue + 1}`)
    }
  }

  return <div ref={ref} className={cn(classes, className)} {...passthroughProps} />
})
Col.displayName = "Col"
